import { getActiveOrgId } from '@/stores/orgStore';
import { useAuthStore } from '@/stores/authStore';

type ApiInit = RequestInit & {
  skipRefresh?: boolean;
  skipRedirect?: boolean; // Don't redirect to /auth on 401
};

let refreshingPromise: Promise<void> | null = null;
let refreshAttempts = 0;
let lastRefreshTime = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_COOLDOWN = 5000; // 5 seconds

// Central redirect hook – swaps to router navigation in the app shell when available
function navigateToAuth() {
  try {
    // Fallback for now; can be replaced by a router-aware helper
    window.location.replace('/auth');
  } catch {}
}

// Circuit breaker for refresh attempts
function canAttemptRefresh(): boolean {
  const now = Date.now();
  if (now - lastRefreshTime > REFRESH_COOLDOWN) {
    refreshAttempts = 0; // Reset counter after cooldown
  }

  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    return false;
  }
  return true;
}

function isAuthEndpoint(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    // Skip refresh for auth endpoints to avoid loops
    // But allow refresh for /api/auth/me since that's how we check auth status
    return u.pathname === '/api/auth/refresh' ||
           u.pathname === '/api/auth/login' ||
           u.pathname === '/api/auth/logout';
  } catch {
    return false;
  }
}

async function refreshSession(skipRedirect = false): Promise<void> {
  if (refreshingPromise) {
    return refreshingPromise;
  }

  refreshAttempts++;
  lastRefreshTime = Date.now();

  refreshingPromise = (async () => {
    const MAX_RETRIES = 3;
    const BASE_DELAY_MS = 1000;
    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (res.ok) {
          refreshAttempts = 0;
          try {
            const payload = await res.json();
            const me = payload?.data;
            if (me) {
              useAuthStore.setState({ user: me });
            }
          } catch {}
          return; // success
        }

        // Definitive auth rejection — don't retry
        if (res.status === 401 || res.status === 403) {
          lastError = new Error('Refresh rejected by server');
          break;
        }

        // 5xx or other server error — retry
        lastError = new Error(`Refresh failed with status ${res.status}`);
      } catch (e: any) {
        // Network error — retry
        lastError = e instanceof Error ? e : new Error('Network error during refresh');
      }

      // Wait before retrying (exponential backoff: 1s, 2s, 4s)
      if (attempt < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, BASE_DELAY_MS * Math.pow(2, attempt - 1)));
      }
    }

    // All retries exhausted — clear auth and redirect
    try { useAuthStore.setState({ user: null }); } catch {}
    const currentPath = window.location.pathname;
    if (!skipRedirect && !currentPath.startsWith('/auth')) {
      navigateToAuth();
    }
    throw lastError || new Error('Refresh failed');
  })();

  try {
    return await refreshingPromise;
  } finally {
    refreshingPromise = null;
  }
}

// --- Main API fetch ---

export async function apiFetch(input: RequestInfo | URL, init?: ApiInit): Promise<Response> {
  const originalUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : String((input as any)?.url || input);

  const headers = new Headers(init?.headers || {});
  // Add org header if not present and we have an active org
  if (!headers.has('X-Organization-Id')) {
    const orgId = getActiveOrgId();
    if (orgId) {
      headers.set('X-Organization-Id', orgId);
    }
  }

  const requestInit: RequestInit = {
    ...init,
    headers,
    credentials: init?.credentials || 'include',
  };

  const res = await fetch(input, requestInit);

  if (res.status !== 401) {
    return res;
  }

  // 401 handling: do not try to refresh for auth endpoints or when explicitly skipped
  if (init?.skipRefresh || isAuthEndpoint(originalUrl)) {
    return res;
  }

  // Note: We can't check httpOnly cookies from JavaScript, so we'll attempt refresh
  // and let the server tell us if there's no valid refresh token

  // Circuit breaker: check if we should attempt refresh
  if (!canAttemptRefresh()) {
    return res;
  }

  // Attempt a single, deduped refresh
  try {
    await refreshSession(init?.skipRedirect);
  } catch {
    return res; // refreshSession already redirected; return original 401 to caller
  }

  // Replay the original request once after successful refresh
  const replayRes = await fetch(input, requestInit);
  return replayRes;
}
